import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, Tabs, Table, Button, Space, Input, Select, message, 
  InputNumber, Checkbox, Modal, Row, Col, Tag, Radio, Divider,
  Spin
} from 'antd';
import { 
  SearchOutlined, PrinterOutlined, EyeOutlined, 
  DeleteOutlined, PlusOutlined, ReloadOutlined 
} from '@ant-design/icons';
import { api, FILE_BASE_URL } from '../services/api';
import { fetchProductVariants, fetchProductImages, type ProductVariantDto } from '../services/productService';
import { useReactToPrint } from 'react-to-print';
import Barcode from 'react-barcode';
import { useCurrency } from '../contexts/CurrencyContext';

const { TabPane } = Tabs;
const { Option } = Select;

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

interface Warehouse {
  id: number;
  warehouseName: string;
}

interface Product {
  id: number;
  productCode: string;
  productName: string;
  barcode?: string;
  categoryName?: string;
  isActive: boolean;
  totalStock?: number;
  isPriceVariant: boolean;
  price?: number;
}

interface Variant {
  id: number;
  sku: string;
  price: number;
  stock: number;
  values: Array<{ variantName: string; variantValue: string }>;
}

export type ImageFieldOption = 'product_id' | 'original_file_name' | 'original_file_path' | 'thumbnail_file_path';

interface LabelItem {
  id: string;
  productId: number;
  productCode: string;
  productName: string;
  barcode?: string;
  variantSku?: string;
  variantInfo?: string;
  variantId?: number;
  price?: number;
  quantity: number;
  /** Görsel alanı - ana ürün görseli (API'den doldurulur) */
  primaryImageOriginalFileName?: string;
  primaryImageOriginalPath?: string;
  primaryImageThumbnailPath?: string;
}

interface LabelTemplate {
  id: string;
  name: string;
  size: { width: number; height: number };
  fields: {
    showProductName: boolean;
    showProductCode: boolean;
    showBarcode: boolean;
    showPrice: boolean;
    showCategory: boolean;
    /** Barkod gibi Görsel alanı: ayrı ayrı product_id, original_file_name, original_file_path, thumbnail_file_path seçilebilir */
    showImage: boolean;
    imageField: ImageFieldOption;
  };
}

const PrintLabels: React.FC = () => {
  const [activeTab, setActiveTab] = useState('bulk');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [labelItems, setLabelItems] = useState<LabelItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | undefined>();
  const [activeOnly, setActiveOnly] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [bulkQuantity, setBulkQuantity] = useState(1);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('standard');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [variantsByProduct, setVariantsByProduct] = useState<Record<number, ProductVariantDto[]>>({});
  const { baseCurrencySymbol } = useCurrency();

  const printRef = useRef<HTMLDivElement>(null);

  const [templates, setTemplatesState] = useState<LabelTemplate[]>(() => [
    {
      id: 'standard',
      name: 'Standart (50x30mm)',
      size: { width: 50, height: 30 },
      fields: {
        showProductName: true,
        showProductCode: true,
        showBarcode: true,
        showPrice: true,
        showCategory: false,
        showImage: false,
        imageField: 'thumbnail_file_path',
      },
    },
    {
      id: 'small',
      name: 'Küçük (40x25mm)',
      size: { width: 40, height: 25 },
      fields: {
        showProductName: false,
        showProductCode: false,
        showBarcode: true,
        showPrice: true,
        showCategory: false,
        showImage: false,
        imageField: 'thumbnail_file_path',
      },
    },
    {
      id: 'mini',
      name: 'Mini (45x10mm)',
      size: { width: 45, height: 10 },
      fields: {
        showProductName: false,
        showProductCode: true,
        showBarcode: false,
        showPrice: false,
        showCategory: false,
        showImage: false,
        imageField: 'thumbnail_file_path',
      },
    },
    {
      id: 'mini-barcode',
      name: 'Mini Barkodlu (45x10mm)',
      size: { width: 45, height: 10 },
      fields: {
        showProductName: false,
        showProductCode: true,
        showBarcode: true,
        showPrice: false,
        showCategory: false,
        showImage: false,
        imageField: 'thumbnail_file_path',
      },
    },
    {
      id: 'large',
      name: 'Büyük (60x40mm)',
      size: { width: 60, height: 40 },
      fields: {
        showProductName: true,
        showProductCode: true,
        showBarcode: true,
        showPrice: true,
        showCategory: true,
        showImage: false,
        imageField: 'thumbnail_file_path',
      },
    },
  ]);

  const currentTemplate = templates.find(t => t.id === selectedTemplate) || templates[0];

  const setTemplateImageFields = (updates: { showImage?: boolean; imageField?: ImageFieldOption }) => {
    setTemplatesState(prev => prev.map(t =>
      t.id === selectedTemplate
        ? { ...t, fields: { ...t.fields, ...updates } }
        : t
    ));
  };

  useEffect(() => {
    loadCategories();
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (activeTab === 'bulk') {
      loadProducts();
    }
  }, [activeTab, pagination.current, pagination.pageSize, selectedCategory, selectedWarehouse, activeOnly, inStockOnly]);

  // Etiketlere ana ürün görseli yükle (product_id, original_file_name, original_file_path, thumbnail_file_path için)
  useEffect(() => {
    if (labelItems.length === 0) return;
    const productIds = Array.from(new Set(labelItems.map(l => l.productId))).filter(
      id => !labelItems.some(l => l.productId === id && l.primaryImageThumbnailPath != null)
    );
    if (productIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const byProduct: Record<number, { originalFileName: string; originalFilePath: string; thumbnailFilePath: string }> = {};
      for (const pid of productIds) {
        if (cancelled) return;
        try {
          const images = await fetchProductImages(pid);
          const primary = images.find(i => i.isPrimary) || images[0];
          if (primary)
            byProduct[pid] = {
              originalFileName: primary.originalFileName,
              originalFilePath: primary.originalFilePath,
              thumbnailFilePath: primary.thumbnailFilePath,
            };
        } catch {
          // skip
        }
      }
      if (cancelled) return;
      setLabelItems(prev => prev.map(item => {
        const img = byProduct[item.productId];
        if (!img) return item;
        return {
          ...item,
          primaryImageOriginalFileName: img.originalFileName,
          primaryImageOriginalPath: img.originalFilePath,
          primaryImageThumbnailPath: img.thumbnailFilePath,
        };
      }));
    })();
    return () => { cancelled = true; };
  }, [labelItems.length, labelItems.map(l => l.id).join(',')]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get<Category[]>('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const { data } = await api.get<Warehouse[]>('/warehouses');
      setWarehouses(data);
    } catch (error) {
      console.error('Depolar yüklenirken hata:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      };

      if (selectedCategory) params.categoryId = selectedCategory;
      if (searchText.trim()) params.search = searchText.trim();
      if (activeOnly) params.isActive = true;

      const { data } = await api.get('/products', { params });
      
      let filteredProducts = data.data || [];
      
      if (inStockOnly) {
        filteredProducts = filteredProducts.filter((p: Product) => (p.totalStock || 0) > 0);
      }

      setProducts(filteredProducts);
      setPagination(prev => ({
        ...prev,
        total: data.totalCount || filteredProducts.length,
      }));
    } catch (error) {
      message.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadProducts();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleSelectProduct = (productId: number, selected: boolean) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleBulkQuantityChange = (quantity: number) => {
    setBulkQuantity(quantity);
  };

  const handleApplyBulkQuantity = () => {
    if (selectedProducts.length === 0) {
      message.warning('Lütfen en az bir ürün seçin');
      return;
    }

    const newLabels: LabelItem[] = selectedProducts.flatMap(productId => {
      const product = products.find(p => p.id === productId);
      if (!product) return [];

      return Array.from({ length: bulkQuantity }, (_, index) => ({
        id: `${product.id}-${Date.now()}-${index}`,
        productId: product.id,
        productCode: product.productCode,
        productName: product.productName,
        barcode: product.barcode,
        price: product.price,
        quantity: 1,
      }));
    });

    setLabelItems(prev => [...prev, ...newLabels]);
    message.success(`${selectedProducts.length} ürün için ${bulkQuantity} adet etiket eklendi`);
    setSelectedProducts([]);
  };

  const handleAddAllActiveProducts = async () => {
    try {
      setLoading(true);
      message.loading({ content: 'Tüm aktif ürünler yükleniyor...', key: 'addAll', duration: 0 });

      // Tüm aktif ürünleri ve varyantları çek
      const params: any = {
        isActive: true,
      };

      if (selectedCategory) params.categoryId = selectedCategory;
      if (selectedWarehouse) params.warehouseId = selectedWarehouse;
      if (inStockOnly) params.inStockOnly = true;

      const { data } = await api.get('/products', { params: { ...params, pageSize: 9999 } });
      const allProducts: Product[] = data.data || [];

      let totalLabelsAdded = 0;
      const newLabels: LabelItem[] = [];

      for (const product of allProducts) {
        if (product.isPriceVariant) {
          // Varyantlı ürün - her varyantın stoğu kadar etiket ekle
          const variants = await fetchProductVariants(product.id);
          
          for (const variant of variants) {
            const stockCount = variant.stock || 0;
            if (stockCount > 0) {
              const variantInfo = variant.values.map(v => `${v.variantValue}`).join('-');
              
              for (let i = 0; i < stockCount; i++) {
                newLabels.push({
                  id: `all-variant-${variant.id}-${Date.now()}-${i}-${Math.random()}`,
                  productId: product.id,
                  productCode: variant.sku,
                  productName: product.productName,
                  barcode: variant.sku,
                  variantSku: variant.sku,
                  variantInfo: variantInfo,
                  variantId: variant.id,
                  price: variant.price,
                  quantity: 1,
                });
                totalLabelsAdded++;
              }
            }
          }
        } else {
          // Normal ürün - stok sayısı kadar etiket ekle
          const stockCount = product.totalStock || 0;
          if (stockCount > 0) {
            for (let i = 0; i < stockCount; i++) {
              newLabels.push({
                id: `all-${product.id}-${Date.now()}-${i}-${Math.random()}`,
                productId: product.id,
                productCode: product.productCode,
                productName: product.productName,
                barcode: product.barcode,
                price: product.price,
                quantity: 1,
              });
              totalLabelsAdded++;
            }
          }
        }
      }

      setLabelItems(prev => [...prev, ...newLabels]);
      message.success({ 
        content: `${allProducts.length} ürün için toplam ${totalLabelsAdded} adet etiket eklendi`, 
        key: 'addAll',
        duration: 3 
      });
    } catch (error: any) {
      console.error('Hata:', error);
      message.error({ 
        content: 'Ürünler yüklenirken hata oluştu', 
        key: 'addAll',
        duration: 3 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToLabels = (product: Product, quantity: number = 1) => {
    const newLabels: LabelItem[] = Array.from({ length: quantity }, (_, index) => ({
      id: `${product.id}-${Date.now()}-${index}`,
      productId: product.id,
      productCode: product.productCode,
      productName: product.productName,
      barcode: product.barcode,
      price: product.price,
      quantity: 1,
    }));

    setLabelItems(prev => [...prev, ...newLabels]);
    message.success(`${product.productName} için ${quantity} adet etiket eklendi`);
  };

  const handleAddVariantToLabels = (product: Product, variant: ProductVariantDto, quantity: number = 1) => {
    const variantInfo = variant.values.map(v => `${v.variantValue}`).join('-');
    const newLabels: LabelItem[] = Array.from({ length: quantity }, (_, index) => ({
      id: `variant-${variant.id}-${Date.now()}-${index}`,
      productId: product.id,
      productCode: variant.sku,
      productName: product.productName,
      barcode: variant.sku,
      variantSku: variant.sku,
      variantInfo: variantInfo,
      variantId: variant.id,
      price: variant.price,
      quantity: 1,
    }));

    setLabelItems(prev => [...prev, ...newLabels]);
    message.success(`${product.productName} (${variantInfo}) için ${quantity} adet etiket eklendi`);
  };

  const handleRemoveLabel = (labelId: string) => {
    setLabelItems(prev => prev.filter(item => item.id !== labelId));
  };

  const handleClearLabels = () => {
    setLabelItems([]);
    message.info('Tüm etiketler temizlendi');
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Ürün Etiketleri',
  });

  const handlePreview = () => {
    if (labelItems.length === 0) {
      message.warning('Yazdırılacak etiket bulunamadı');
      return;
    }
    setPreviewVisible(true);
  };

  const handlePrintAction = () => {
    if (labelItems.length === 0) {
      message.warning('Yazdırılacak etiket bulunamadı');
      return;
    }
    if (handlePrint) {
      handlePrint();
    }
  };

  const getSortedCategoriesHierarchically = (categories: Category[]) => {
    const result: Array<Category & { level: number; fullName: string }> = [];
    
    const addCategoryAndChildren = (parentId: number | null | undefined, level: number = 0) => {
      const children = categories
        .filter(c => c.parentId === parentId)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
      
      children.forEach(cat => {
        let fullName = cat.categoryName;
        let currentCat = cat;
        while (currentCat.parentId) {
          const parent = categories.find(c => c.id === currentCat.parentId);
          if (!parent) break;
          fullName = `${parent.categoryName} > ${fullName}`;
          currentCat = parent;
        }
        
        result.push({ ...cat, level, fullName });
        addCategoryAndChildren(cat.id, level + 1);
      });
    };
    
    addCategoryAndChildren(null, 0);
    addCategoryAndChildren(undefined, 0);
    
    return result;
  };

  const categoriesWithHierarchy = getSortedCategoriesHierarchically(categories);

  const bulkColumns = [
    {
      title: (
        <Checkbox
          checked={selectedProducts.length === products.length && products.length > 0}
          indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'select',
      width: 50,
      render: (_: any, record: Product) => (
        <Checkbox
          checked={selectedProducts.includes(record.id)}
          onChange={(e) => handleSelectProduct(record.id, e.target.checked)}
          disabled={record.isPriceVariant}
        />
      ),
    },
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 150,
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Barkod',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 150,
      render: (_: any, record: Product) => {
        if (record.isPriceVariant) {
          return <Tag color="blue">Varyantlı</Tag>;
        }
        return record.barcode || '-';
      },
    },
    {
      title: 'Stok',
      dataIndex: 'totalStock',
      key: 'totalStock',
      width: 80,
      render: (stock: number) => stock || 0,
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'İşlem',
      key: 'action',
      width: 200,
      render: (_: any, record: Product) => {
        if (record.isPriceVariant) {
          return <Tag color="orange">Varyantları seç →</Tag>;
        }
        return (
          <Space>
            <InputNumber
              min={1}
              max={100}
              defaultValue={1}
              size="small"
              style={{ width: 60 }}
              id={`qty-${record.id}`}
            />
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                const input = document.getElementById(`qty-${record.id}`) as HTMLInputElement;
                const qty = parseInt(input.value) || 1;
                handleAddToLabels(record, qty);
              }}
            >
              Ekle
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>🏷️ Etiket Yazdırma</h1>
        <Space>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={handleAddAllActiveProducts}
            loading={loading}
            style={{ background: '#52c41a', color: 'white', borderColor: '#52c41a' }}
          >
            Tüm Aktif Ürünleri Ekle (Stok Kadar)
          </Button>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={loadProducts}
          >
            Yenile
          </Button>
          <Button
            type="default"
            icon={<EyeOutlined />}
            onClick={handlePreview}
            disabled={labelItems.length === 0}
          >
            Önizleme ({labelItems.length})
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintAction}
            disabled={labelItems.length === 0}
          >
            Yazdır ({labelItems.length})
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Toplu Yazdırma" key="bulk">
          <Card style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap>
                <Input
                  placeholder="Ürün ara..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                  style={{ width: 250 }}
                />
                <Select
                  placeholder="Kategori seçiniz"
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  style={{ width: 250 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const categoryName = categoriesWithHierarchy.find(c => c.id === option?.value)?.categoryName || '';
                    return categoryName.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {categoriesWithHierarchy.map(category => (
                    <Option key={category.id} value={category.id}>
                      <span style={{ paddingLeft: `${category.level * 20}px` }}>
                        {category.level > 0 && '└─ '}
                        {category.categoryName}
                      </span>
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="Depo seçiniz"
                  value={selectedWarehouse}
                  onChange={setSelectedWarehouse}
                  style={{ width: 200 }}
                  allowClear
                >
                  {warehouses.map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      {warehouse.warehouseName}
                    </Option>
                  ))}
                </Select>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Ara
                </Button>
              </Space>

              <Space>
                <Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}>
                  Sadece Aktif Ürünler
                </Checkbox>
                <Checkbox checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)}>
                  Sadece Stoğu Olan
                </Checkbox>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                <Space>
                  <span>Toplu İşlem:</span>
                  <Button 
                    size="small" 
                    onClick={() => handleSelectAll(true)}
                    disabled={products.length === 0}
                  >
                    Tümünü Seç
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => handleSelectAll(false)}
                    disabled={selectedProducts.length === 0}
                  >
                    Seçimi Temizle
                  </Button>
                  <span>|</span>
                  <span>Adet:</span>
                  <InputNumber
                    min={1}
                    max={100}
                    value={bulkQuantity}
                    onChange={(val) => setBulkQuantity(val || 1)}
                    style={{ width: 70 }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleApplyBulkQuantity}
                    disabled={selectedProducts.length === 0}
                  >
                    Uygula ({selectedProducts.length} ürün)
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>

          <Card>
            <Table
              columns={bulkColumns}
              dataSource={products}
              rowKey="id"
              loading={loading}
              expandable={{
                expandedRowKeys,
                rowExpandable: (record) => record.isPriceVariant,
                onExpand: async (expanded, record) => {
                  if (!record.isPriceVariant) return;

                  if (expanded) {
                    if (!variantsByProduct[record.id]) {
                      const variants = await fetchProductVariants(record.id);
                      setVariantsByProduct(prev => ({ ...prev, [record.id]: variants }));
                      if (variants.length === 0) {
                        setExpandedRowKeys(keys => keys.filter(k => k !== record.id));
                        message.info('Bu ürünün varyantı bulunmamaktadır.');
                        return;
                      }
                    }
                    setExpandedRowKeys(keys => [...keys, record.id]);
                  } else {
                    setExpandedRowKeys(keys => keys.filter(k => k !== record.id));
                  }
                },
                expandedRowRender: (record: Product) => {
                  const variants = variantsByProduct[record.id] || [];
                  if (variants.length === 0) {
                    return (
                      <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
                        Bu ürünün varyantı bulunmamaktadır.
                      </div>
                    );
                  }
                  return (
                    <Table
                      size="small"
                      rowKey="id"
                      columns={[
                        { 
                          title: 'SKU', 
                          dataIndex: 'sku', 
                          width: 150,
                          render: (sku: string) => <strong>{sku}</strong>
                        },
                        { 
                          title: 'Varyant Değerleri', 
                          render: (_: any, variant: ProductVariantDto) => (
                            <Space wrap>
                              {variant.values.map((v, idx) => (
                                <Tag key={idx} color="blue">
                                  {v.variantName}: {v.variantValue}
                                </Tag>
                              ))}
                            </Space>
                          )
                        },
                        { 
                          title: 'Fiyat', 
                          dataIndex: 'price', 
                          width: 120, 
                          render: (val: number) => `${baseCurrencySymbol}${val.toFixed(2)}` 
                        },
                        { 
                          title: 'Stok', 
                          dataIndex: 'stock', 
                          width: 100 
                        },
                        {
                          title: 'İşlem',
                          key: 'action',
                          width: 200,
                          render: (_: any, variant: ProductVariantDto) => (
                            <Space>
                              <InputNumber
                                min={1}
                                max={100}
                                defaultValue={1}
                                size="small"
                                style={{ width: 60 }}
                                id={`qty-variant-${variant.id}`}
                              />
                              <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                  const input = document.getElementById(`qty-variant-${variant.id}`) as HTMLInputElement;
                                  const qty = parseInt(input.value) || 1;
                                  handleAddVariantToLabels(record, variant, qty);
                                }}
                              >
                                Ekle
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                      dataSource={variants}
                      pagination={false}
                    />
                  );
                }
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Toplam ${total} ürün`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              onChange={handleTableChange}
            />
          </Card>
        </TabPane>

        <TabPane tab="Tek Tek Seç" key="individual">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="Ürün ara ve ekle..."
                prefix={<SearchOutlined />}
                enterButton="Ara"
                size="large"
                onSearch={(value) => {
                  setSearchText(value);
                  setActiveTab('bulk');
                }}
              />
              <div style={{ marginTop: 8, color: '#888', fontSize: '12px' }}>
                💡 İpucu: Ürün aramak için yukarıdaki alanı kullanın ve "Toplu Yazdırma" sekmesinden ekleyin
              </div>
            </div>
          </Card>
        </TabPane>
      </Tabs>

      {/* Yazdırılacak Etiketler Listesi */}
      {labelItems.length > 0 && (
        <Card
          title={`Yazdırılacak Etiketler (${labelItems.length})`}
          extra={
            <Button danger size="small" onClick={handleClearLabels}>
              Tümünü Temizle
            </Button>
          }
          style={{ marginTop: 16 }}
        >
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Table
              size="small"
              columns={[
                { 
                  title: 'Ürün Kodu / SKU', 
                  dataIndex: 'productCode', 
                  width: 150,
                  render: (code: string, record: LabelItem) => (
                    <div>
                      <div><strong>{code}</strong></div>
                      {record.variantInfo && (
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          <Tag color="blue" style={{ marginTop: 4 }}>{record.variantInfo}</Tag>
                        </div>
                      )}
                    </div>
                  )
                },
                { title: 'Ürün Adı', dataIndex: 'productName' },
                { title: 'Barkod', dataIndex: 'barcode', width: 150 },
                { 
                  title: 'Fiyat', 
                  dataIndex: 'price', 
                  width: 100,
                  render: (price: number) => price ? `${baseCurrencySymbol}${price.toFixed(2)}` : '-'
                },
                {
                  title: 'İşlem',
                  key: 'action',
                  width: 80,
                  render: (_: any, record: LabelItem) => (
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveLabel(record.id)}
                    />
                  ),
                },
              ]}
              dataSource={labelItems}
              rowKey="id"
              pagination={false}
            />
          </div>
        </Card>
      )}

      {/* Önizleme Modal */}
      <Modal
        title="Etiket Önizleme"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Kapat
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintAction}>
            Yazdır
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Etiket Şablonu:</strong>
          </div>
          <Radio.Group
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {templates.map(template => (
                <Radio key={template.id} value={template.id}>
                  <strong>{template.name}</strong>
                  <div style={{ fontSize: '12px', color: '#888', marginLeft: 24 }}>
                    {template.id === 'mini' ? (
                      <>
                        ✓ Ürün Kodu ✓ Varyant (Ortalı - A4'te 4×20=80 adet)
                      </>
                    ) : template.id === 'mini-barcode' ? (
                      <>
                        ✓ Barkod ✓ ID ✓ Kod ✓ Varyant (Yan yana - A4'te 4×20=80 adet)
                      </>
                    ) : (
                      <>
                        {template.fields.showProductName && '✓ Ürün Adı '}
                        {template.fields.showProductCode && '✓ Ürün Kodu '}
                        {template.fields.showBarcode && '✓ Barkod '}
                        {template.fields.showImage && `✓ Görsel (${template.fields.imageField === 'product_id' ? 'Ürün ID' : template.fields.imageField === 'original_file_name' ? 'Dosya adı' : template.fields.imageField === 'original_file_path' ? 'Orijinal' : 'Thumbnail'}) `}
                        {template.fields.showPrice && '✓ Fiyat '}
                        {template.fields.showCategory && '✓ Kategori'}
                      </>
                    )}
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <Divider orientation="left">Görsel alanı (Barkod gibi)</Divider>
          <Row gutter={16} align="middle">
            <Col>
              <Checkbox
                checked={currentTemplate.fields.showImage}
                onChange={e => setTemplateImageFields({ showImage: e.target.checked })}
              >
                Görsel göster
              </Checkbox>
            </Col>
            <Col>
              <span style={{ marginRight: 8 }}>Görsel alanı:</span>
              <Select
                value={currentTemplate.fields.imageField}
                onChange={(v: ImageFieldOption) => setTemplateImageFields({ imageField: v })}
                style={{ minWidth: 220 }}
                options={[
                  { value: 'product_id', label: 'Ürün ID (product_id)' },
                  { value: 'original_file_name', label: 'Orijinal dosya adı (original_file_name)' },
                  { value: 'original_file_path', label: 'Orijinal dosya yolu (görsel)' },
                  { value: 'thumbnail_file_path', label: 'Thumbnail dosya yolu (görsel)' },
                ]}
              />
            </Col>
          </Row>
        </div>

        <Divider />

        <div 
          style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            padding: '16px',
            background: '#fafafa'
          }}
        >
          <LabelPreview labels={labelItems} template={currentTemplate} currencySymbol={baseCurrencySymbol} />
        </div>
      </Modal>

      {/* Gizli Yazdırma Alanı */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <style>
            {`
              @media print {
                @page {
                  size: A4;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
            `}
          </style>
          <LabelPreview labels={labelItems} template={currentTemplate} currencySymbol={baseCurrencySymbol} />
        </div>
      </div>
    </div>
  );
};

const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [array];
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// Etiket Önizleme Komponenti
const LabelPreview: React.FC<{ labels: LabelItem[]; template: LabelTemplate; currencySymbol: string }> = ({ labels, template, currencySymbol }) => {
  const isMiniTemplate = template.id === 'mini' || template.id === 'mini-barcode';
  const labelsPerPage = isMiniTemplate ? 80 : labels.length || 1;
  const pages = chunkArray(labels, labelsPerPage);

  const pageWidth = 216;
  const pageHeight = 280;
  const topBottomMargin = 0;
  const leftRightMargin = 7;
  const printableWidth = pageWidth - 2 * leftRightMargin;
  const printableHeight = pageHeight - 2 * topBottomMargin;
  const miniRowHeight = printableHeight / 22;
  const miniLabelHeight = 13;
  const miniLabelWidth = 46;
  const miniVerticalMargin = (miniRowHeight - miniLabelHeight) / 2;
  const totalLabelsWidth = miniLabelWidth * 4;
  const totalGapWidth = printableWidth - totalLabelsWidth;
  const miniColumnGap =
    totalGapWidth >= 0 ? `${totalGapWidth / 3}mm` : '2mm';

  return (
    <>
      {pages.map((pageLabels: LabelItem[], pageIndex: number) => {
        const cells: (LabelItem | null)[] = [];
        // top blank row (4 cells)
        for (let i = 0; i < 4; i += 1) cells.push(null);
        for (let i = 0; i < 80; i += 1) {
          cells.push(pageLabels[i] || null);
        }
        // bottom blank row (4 cells)
        for (let i = 0; i < 4; i += 1) cells.push(null);

        return (
        <div
          key={`page-${pageIndex}`}
          style={{
            pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
            marginBottom: pageIndex < pages.length - 1 ? '12px' : 0,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMiniTemplate ? `repeat(4, ${miniLabelWidth}mm)` : 'repeat(4, 1fr)',
              gridTemplateRows: isMiniTemplate ? `repeat(22, ${miniRowHeight}mm)` : 'auto',
              gridAutoRows: isMiniTemplate ? 'unset' : 'auto',
              columnGap: isMiniTemplate ? miniColumnGap : '8px',
              rowGap: isMiniTemplate ? '0mm' : '8px',
              padding: isMiniTemplate ? '0mm' : '8px',
              width: isMiniTemplate ? `calc(${pageWidth}mm - ${leftRightMargin * 2}mm)` : 'auto',
              height: isMiniTemplate ? `${pageHeight}mm` : 'auto',
              boxSizing: 'border-box',
              margin: '0 auto',
              background: '#fff',
              alignContent: 'start',
            }}
          >
            {cells.map((label, cellIdx) => {
              if (!label) {
                return (
                  <div
                    key={`cell-${cellIdx}`}
                    style={{
                      width: `${miniLabelWidth}mm`,
                      height: `${miniLabelHeight}mm`,
                      margin: `${miniVerticalMargin}mm 0`,
                    }}
                  />
                );
              }
              if (template.id === 'mini-barcode') {
                const barcodeText = label.variantId
                  ? `${label.productCode}-${label.productId}-${label.variantId}`
                  : `${label.productCode}-${label.productId}`;

                return (
                  <div
                    key={label.id}
                    style={{
                      border: '0.5px solid #ddd',
                      width: `${miniLabelWidth}mm`,
                      height: `${miniLabelHeight}mm`,
                      margin: `${miniVerticalMargin}mm 0`,
                      padding: '0 1mm',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                    }}
                  >
                    {label.barcode && (
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Barcode
                          value={barcodeText}
                          width={0.9}
                          height={12}
                          fontSize={0}
                          margin={0}
                          displayValue={false}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: '7px',
                        fontWeight: 600,
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        marginTop: '0.5mm',
                      }}
                    >
                      {label.productCode} {label.variantInfo || ''}
                    </div>
                  </div>
                );
              }

              if (template.id === 'mini') {
                return (
                  <div
                    key={label.id}
                    style={{
                      border: '0.5px solid #ddd',
                      width: `${miniLabelWidth}mm`,
                      height: `${miniLabelHeight}mm`,
                      margin: `${miniVerticalMargin}mm 0`,
                      padding: '0 2mm',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      fontSize: '10px',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      {label.productCode}
                    </div>
                    {label.variantInfo && (
                      <div
                        style={{
                          fontSize: '7px',
                          color: '#555',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          textAlign: 'center',
                          marginTop: '0.5mm',
                        }}
                      >
                        {label.variantInfo}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div
                  key={label.id}
                  style={{
                    border: '1px dashed #ccc',
                    padding: '8px',
                    background: 'white',
                    minHeight: `${template.size.height}mm`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    fontSize: template.id === 'small' ? '8px' : template.id === 'large' ? '11px' : '9px',
                  }}
                >
                  {template.fields.showProductCode && (
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {label.productCode}
                    </div>
                  )}

                  {template.fields.showProductName && (
                    <div
                      style={{
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label.productName}
                      {label.variantInfo && template.id === 'large' && (
                        <div style={{ fontSize: '7px', color: '#666', marginTop: '2px' }}>
                          {label.variantInfo}
                        </div>
                      )}
                    </div>
                  )}

                  {label.variantInfo && !template.fields.showProductName && (
                    <div
                      style={{
                        fontSize: '7px',
                        color: '#666',
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label.variantInfo}
                    </div>
                  )}

                  {template.fields.showBarcode && label.barcode && (
                    <div style={{ textAlign: 'center', margin: '4px 0' }}>
                      <Barcode
                        value={label.barcode}
                        width={1}
                        height={template.id === 'small' ? 20 : template.id === 'large' ? 35 : 25}
                        fontSize={template.id === 'small' ? 8 : 10}
                        margin={0}
                      />
                    </div>
                  )}

                  {template.fields.showImage && (() => {
                    const f = template.fields.imageField;
                    if (f === 'product_id') {
                      return (
                        <div style={{ fontSize: '9px', color: '#666', marginTop: '4px' }}>
                          ID: {label.productId}
                        </div>
                      );
                    }
                    if (f === 'original_file_name') {
                      return (
                        <div style={{ fontSize: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '4px' }}>
                          {label.primaryImageOriginalFileName || '-'}
                        </div>
                      );
                    }
                    if (f === 'original_file_path' && label.primaryImageOriginalPath) {
                      const src = label.primaryImageOriginalPath.startsWith('http') ? label.primaryImageOriginalPath : `${FILE_BASE_URL}${label.primaryImageOriginalPath.startsWith('/') ? '' : '/'}${label.primaryImageOriginalPath}`;
                      return (
                        <div style={{ textAlign: 'center', margin: '4px 0' }}>
                          <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '24mm', objectFit: 'contain' }} />
                        </div>
                      );
                    }
                    if (f === 'thumbnail_file_path' && label.primaryImageThumbnailPath) {
                      const src = label.primaryImageThumbnailPath.startsWith('http') ? label.primaryImageThumbnailPath : `${FILE_BASE_URL}${label.primaryImageThumbnailPath.startsWith('/') ? '' : '/'}${label.primaryImageThumbnailPath}`;
                      return (
                        <div style={{ textAlign: 'center', margin: '4px 0' }}>
                          <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '24mm', objectFit: 'contain' }} />
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {template.fields.showPrice && label.price && (
                    <div
                      style={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: template.id === 'large' ? '14px' : '11px',
                        marginTop: '4px',
                      }}
                    >
                      {currencySymbol}
                      {label.price.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })} 
    </>
  );
};

export default PrintLabels;

